import React from 'react';
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
    Box,
    Typography,
    CircularProgress,
    FormControl,
    FormLabel,
    RadioGroup,
    FormControlLabel,
    Radio,
    TextField,
    Grid,
    Divider,
    Alert
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { tokens } from '../../theme';
import jsPDF from 'jspdf';
import autoTable from "jspdf-autotable";

const ReportPDFExport = ({ open, onClose, matrixData }) => {
    const theme = useTheme();
    const colors = tokens(theme.palette.mode);
    const [isGenerating, setIsGenerating] = React.useState(false);
    const [exportOptions, setExportOptions] = React.useState({
        orientation: 'landscape',
        includeMetadata: true,
        includeSummary: true,
        includeDetails: true,
        title: 'Competency Analyses Report',
        subtitle: ''
    });

    const handleOptionChange = (field, value) => {
        setExportOptions(prev => ({ ...prev, [field]: value }));
    };

    const getStatusColor = (status) => {
        switch (status) {
            case 'met': return [0, 150, 80];  // warm green
            case 'partial': return [245, 158, 11]; // yellow
            case 'critical': return [239, 68, 68]; // red
            default: return [107, 114, 128];       // gray
        }
    };

    const getStatusText = (status) => {
        switch (status) {
            case 'met': return 'Met';
            case 'partial': return 'Partial';
            case 'critical': return 'Critical';
            default: return 'Unknown';
        }
    };

    const generatePDF = async () => {
        if (!matrixData) return;
        setIsGenerating(true);
        console.log("generatePDF ; ", matrixData);
        try {
            const { skills, employees, cells, meta, postTitle } = matrixData;

            const pdf = new jsPDF({
                orientation: exportOptions.orientation,
                unit: 'mm',
                format: 'a4'
            });

            const pageWidth = pdf.internal.pageSize.width;
            const pageHeight = pdf.internal.pageSize.height;
            const margin = 15;

            // Build cell lookup
            const cellMap = {};
            cells.forEach(cell => {
                cellMap[`${cell.employeeId}-${cell.skillId}`] = cell;
            });

            // Stats
            const totalCells = employees.length * skills.length;
            const statusCounts = cells.reduce((acc, cell) => {
                acc[cell.status] = (acc[cell.status] || 0) + 1;
                return acc;
            }, {});
            const metStats = statusCounts.met || 0;
            const partialStats = statusCounts.partial || 0;
            const criticalStats = statusCounts.critical || 0;
            const missingStats = totalCells - cells.length;

            // Header
            pdf.setFontSize(20);
            pdf.setTextColor(40, 40, 40);
            pdf.text(exportOptions.title, margin, margin + 10);

            let yPos = margin + 20;

            if (exportOptions.subtitle) {
                pdf.setFontSize(14);
                pdf.setTextColor(100, 100, 100);
                pdf.text(exportOptions.subtitle, margin, yPos);
                yPos += 10;
            }

            // Metadata
            if (exportOptions.includeMetadata) {
                pdf.setFontSize(12);
                pdf.setTextColor(60, 60, 60);
                [
                    `Post Title: ${postTitle}`,
                    `Generated: ${new Date().toLocaleString()}`,
                    `Skills: ${skills.length} | Employees: ${employees.length}`
                ].forEach(text => {
                    pdf.text(text, margin, yPos);
                    yPos += 6;
                });
                yPos += 5;
            }

            // Summary
            if (exportOptions.includeSummary) {
                pdf.setFontSize(14);
                pdf.setTextColor(40, 40, 40);
                pdf.text('Summary Statistics', margin, yPos);
                yPos += 10;

                const summaryData = [
                    ['Status', 'Count', 'Percentage'],
                    ['Met', metStats.toString(), `${Math.round((metStats / totalCells) * 100)}%`],
                    ['Partial', partialStats.toString(), `${Math.round((partialStats / totalCells) * 100)}%`],
                    ['Critical', criticalStats.toString(), `${Math.round((criticalStats / totalCells) * 100)}%`],
                    ['No Data', missingStats.toString(), `${Math.round((missingStats / totalCells) * 100)}%`],
                    ['Total', totalCells.toString(), '100%']
                ];

                const summaryTable = autoTable(pdf, {
                    startY: yPos,
                    head: [summaryData[0]],
                    body: summaryData.slice(1),
                    margin: { left: margin, right: margin },
                    headStyles: { fillColor: [255, 152, 0], textColor: [255, 255, 255] },
                    bodyStyles: { textColor: [60, 60, 60] },
                    columnStyles: {
                        0: { cellWidth: 40 },
                        1: { cellWidth: 30, halign: 'center' },
                        2: { cellWidth: 40, halign: 'center' }
                    }
                });

                yPos = summaryTable?.lastAutoTable?.finalY
                    ? summaryTable.lastAutoTable.finalY + 50
                    : yPos + 50;
            }

            if (yPos > pageHeight - 100) {
                pdf.addPage();
                yPos = margin + 10;
            }

            // Matrix
            if (exportOptions.includeDetails) {
                pdf.setFontSize(14);

                const matrixHeaders = ['Employee', ...skills.map(skill => `${skill.name}(${skill.required_min}-${skill.required_max})`)];
                const matrixBody = employees.map(employee => {
                    const row = [`${employee.name}\n${employee.email || 'No email'}`];
                    skills.forEach(skill => {
                        const cell = cellMap[`${employee.id}-${skill.id}`];
                        row.push(cell ? `${cell.current}\n${getStatusText(cell.status)}` : '-\nNo Data');
                    });
                    return row;
                });

                const matrixTable = autoTable(pdf, {
                    startY: yPos,
                    head: [matrixHeaders],
                    body: matrixBody,
                    margin: { left: margin, right: margin },
                    headStyles: { fillColor: [255, 152, 0], textColor: [255, 255, 255], fontSize: 9,  halign: 'center', cellPadding: 3 },
                    bodyStyles: { fontSize: 9, cellPadding: 2, textColor: [60, 60, 60], halign: 'center' },
                    columnStyles: { 0: { cellWidth: exportOptions.orientation === 'landscape' ? 50 : 45, fontStyle: 'bold' } },
                    didParseCell: function (data) {
                        if (data.section === 'body' && data.column.index > 0) {
                            const employee = employees[data.row.index];
                            const skill = skills[data.column.index - 1];
                            const cell = cellMap[`${employee.id}-${skill.id}`];
                            if (cell) {
                                const statusColor = getStatusColor(cell.status);
                                data.cell.styles.textColor = statusColor;
                            } else {
                                data.cell.styles.textColor = [107, 114, 128]; // gray for no data
                            }
                        }
                    },
                    styles: { overflow: 'linebreak', cellWidth: 'wrap' }
                });

                yPos = matrixTable?.lastAutoTable?.finalY
                    ? matrixTable.lastAutoTable.finalY + 50
                    : yPos + 50;

            }

            // Footer
            const pageCount = pdf.internal.getNumberOfPages();
            for (let i = 1; i <= pageCount; i++) {
                pdf.setPage(i);
                pdf.setFontSize(8);
                pdf.setTextColor(120, 120, 120);
                pdf.text(
                    `Page ${i} of ${pageCount} | Generated by Competency Management System`,
                    pageWidth - margin,
                    pageHeight - 10,
                    { align: 'right' }
                );
            }

            const fileName = `competency-analyses-post-${postTitle}-${new Date().toISOString().split('T')[0]}.pdf`;
            pdf.save(fileName);

        } catch (error) {
            console.error('Error generating PDF:', error);
        } finally {
            setIsGenerating(false);
            onClose();
        }
    };

    return (
        <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
            <DialogTitle sx={{ color: colors.grey[theme.palette.mode === 'dark' ? 700 : 600] }}>
                Export Competency analyses to PDF
            </DialogTitle>
            <DialogContent>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3, mt: 1 }}>
                    {!matrixData && <Alert severity="warning">No analyses data available. Please generate a competency analyses first.</Alert>}
                    {matrixData && (
                        <>
                            <TextField label="Report Title" value={exportOptions.title}
                                       onChange={(e) => handleOptionChange('title', e.target.value)} fullWidth variant="outlined" />
                            <TextField label="Subtitle (Optional)" value={exportOptions.subtitle}
                                       onChange={(e) => handleOptionChange('subtitle', e.target.value)}
                                       fullWidth variant="outlined" placeholder="e.g., Q4 2024 Review" />
                            <Divider />
                            <FormControl component="fieldset">
                                <FormLabel component="legend" sx={{ color: colors.grey[700] }}>Page Orientation</FormLabel>
                                <RadioGroup value={exportOptions.orientation}
                                            onChange={(e) => handleOptionChange('orientation', e.target.value)} row>
                                    <FormControlLabel value="landscape" control={<Radio />} label="Landscape (Recommended)" />
                                    <FormControlLabel value="portrait" control={<Radio />} label="Portrait" />
                                </RadioGroup>
                            </FormControl>
                            <Divider />
                            <Box>
                                <Typography variant="h6" sx={{ mb: 2, color: colors.grey[700] }}>Report Sections</Typography>
                                <Grid container spacing={2}>
                                    <Grid item xs={12}>
                                        <FormControlLabel
                                            control={<input type="checkbox" checked={exportOptions.includeMetadata}
                                                            onChange={(e) => handleOptionChange('includeMetadata', e.target.checked)} />}
                                            label="Include Metadata (Post ID, Date)" />
                                    </Grid>
                                    <Grid item xs={12}>
                                        <FormControlLabel
                                            control={<input type="checkbox" checked={exportOptions.includeSummary}
                                                            onChange={(e) => handleOptionChange('includeSummary', e.target.checked)} />}
                                            label="Include Summary Statistics" />
                                    </Grid>
                                    <Grid item xs={12}>
                                        <FormControlLabel
                                            control={<input type="checkbox" checked={exportOptions.includeDetails}
                                                            onChange={(e) => handleOptionChange('includeDetails', e.target.checked)} />}
                                            label="Include Detailed analyses Table" />
                                    </Grid>
                                </Grid>
                            </Box>
                            <Box sx={{ p: 2, borderRadius: 1, border: `1px solid ${colors.primary[300]}` }}>
                                <Typography variant="body2" sx={{ color: colors.grey[700], mb: 1 }}>
                                    <strong>Report Preview:</strong>
                                </Typography>
                                <Typography variant="body2" sx={{ color: colors.grey[600] }}>
                                    • Post #{matrixData.postTitle} with {matrixData.skills?.length} skills and {matrixData.employees?.length} employees<br />
                                    • {matrixData.cells?.length} competency assessments<br />
                                    • Generated on {new Date().toLocaleDateString()}
                                </Typography>
                            </Box>
                        </>
                    )}
                </Box>
            </DialogContent>
            <DialogActions sx={{ p: 3, gap: 1 }}>
                <Button onClick={onClose} disabled={isGenerating} sx={{ color: colors.grey[600] }}>Cancel</Button>
                <Button onClick={generatePDF} disabled={!matrixData || isGenerating}
                        variant="contained" startIcon={isGenerating ? <CircularProgress size={16} /> : null}
                        sx={{
                            backgroundColor: colors.redAccent[500], color: colors.grey[50],
                            '&:hover': { backgroundColor: colors.redAccent[600] },
                            '&:disabled': { backgroundColor: colors.grey[400] }
                        }}>
                    {isGenerating ? 'Generating PDF...' : 'Export PDF'}
                </Button>
            </DialogActions>
        </Dialog>
    );
};

export default ReportPDFExport;
